<template>
  <b-modal ref="StockModal" size="lg" @shown="shown" :no-close-on-backdrop="true" :no-enforce-focus="true">
    <div slot="modal-title">
      <div class="d-flex">
        <h5 class="modal-title">{{$t('Stock')}}</h5>
      </div>
    </div>
    <div>
      <div class="w-25">
        <label>{{$t('labels.common.smart_search')}}
          <smart-search-badge></smart-search-badge>
        </label>
        <div class="input-group w-100">
          <i class="fa fa-search" @click="fetchPackages"></i>
          <input type="text" class="form-control" placeholder="" ref="smart_search"
                 @keyup.enter="fetchPackages"/>
        </div>
      </div>
      <div class="d-flex justify-content-between align-items-center mt-2">
        <input class="form-control w-25" placeholder="" @input="setQuickFilter">
        <div class="d-flex justify-content-end align-items-center">
          <span v-for="item in selectedData" :key="item.label" class="selected-info__item mr-1">
            {{$t(`labels.common.${item.label}`)}}: {{item.value.toLocaleString('ru-RU')}}
          </span>
          <save-columns-btn :grid="gridOptions" :grid-name="gridName" class="ml-1"></save-columns-btn>
        </div>
      </div>
      <div class="mt-2">
        <ag-grid-vue class="ag-theme-balham grid" :columnDefs="columns" :rowData="rowData"
                     :gridOptions="gridOptions"
                     @selectionChanged="selectionChangedOperations">
        </ag-grid-vue>
      </div>
    </div>
    <div slot="modal-footer" class="w-100 d-flex justify-content-end align-items-center">
      <div class="d-flex align-items-center">
        <input type="checkbox" ref="clone_packets" id="clone-packets">
        <label class="ml-1" for="clone-packets">{{$t('labels.operations.duplicate_package_nr')}}</label>
      </div>
      <b-btn size="sm" class="ml-2" variant="success" @click="addPackagesToOperation"
             title="Add packages to operation">
        {{$t('buttons.common.add')}}
      </b-btn>
    </div>
  </b-modal>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import { handlerResponse, sumTotalData, getFilteredSelectedNodes, setNotifyData } from '@/helpers/commonHelpers';
  import { formatNumber, alignCells, autoResize, notificationsRenderer } from '@/helpers/renderHelpers';
  import SaveColumnsBtn from '@/components/common/SaveColumnsBtn';
  import SmartSearchBadge from '@/components/common/SmartSearchBadge';
  import gridProps from '@/gridProps';

  export default {
    components: {AgGridVue, SaveColumnsBtn, SmartSearchBadge},
    props: {
      fetchCallback: {
        type: Function,
        required: true
      },
      operationId: {
        type: [Number, String],
        required: true
      }
    },
    data() {
      return {
        selectedData: [
          {
            label: 'selected_packets',
            value: 0
          },
          {
            label: 'amount',
            value: 0,
            key: 'pieces'
          },
          {
            label: 'm',
            value: 0,
            key: 'lm'
          },
          {
            label: 'm2',
            value: 0,
            key: 'm2'
          },
          {
            label: 'm3',
            value: 0,
            key: 'm3'
          }
        ],
        columns: [],
        rowData: [],
        gridOptions: {
          rowSelection: 'multiple',
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            valueFormatter(params) {
              return formatNumber(params.value, null);
            },
            cellStyle(params) {
              return alignCells(params);
            }
          },
          onRowDataChanged() {
            if (this.rowData && this.rowData.length) {
              autoResize(this);
            }
          },
          ...gridProps
        },
        gridName: ''
      }
    },
    methods: {
      cleanSelectedData() {
        this.selectedData = this.selectedData.map(item => {
          item.value = 0;
          return item;
        });
      },
      shown() {
        this.cleanSelectedData();
      },
      async fetchPackages() {
        const loader = this.$loading.show();
        try {
          const params = {
            smart_search: this.$refs['smart_search'].value
          }
          const response = await this.$http.get(`woodware/stock_packages_for_operation/${this.operationId}/`, {params})
            .then(handlerResponse);
          const {data, grid_name, used_columns} = response;
          this.columns = used_columns;
          this.rowData = data;
          this.gridName = grid_name;
        } catch (e) {
          notificationsRenderer(e.data, 'error');
        } finally {
          loader.hide();
        }
      },
      setQuickFilter({target: {value}}) {
        this.gridOptions.api.setQuickFilter(value);
      },
      addPackagesToOperation() {
        const selectedRows = getFilteredSelectedNodes(this.gridOptions);
        if (selectedRows.length) {
          const loader = this.$loading.show();
          const packets = selectedRows.map(item => item.data.id);
          const payload = {
            packets,
            movement: this.operationId,
            clone_packets: this.$refs['clone_packets'].checked
          }
          this.$http.post(`woodware/update_movement_data_on_stock_from_stock/`, payload)
            .then(handlerResponse).then(async (response) => {
            const notifyType = response.fully_succeeded ? 'success' : 'warn';
            this.$notify(setNotifyData(response.message, '', notifyType));
            await this.fetchCallback();
            this.$refs['StockModal'].hide();
          }).catch(e => {
            notificationsRenderer(e.data, 'error');
          }).finally(() => {
            loader.hide();
          });
        } else {
          this.$notify(setNotifyData(this.$i18n.t(`notifications.packages.select_for_add_to_operation`), '', 'warn'));
        }
      },
      selectionChangedOperations() {
        const selectedPackets = this.gridOptions.api.getSelectedRows();
        this.selectedData = sumTotalData(selectedPackets, this.selectedData);
      }
    },
    mounted() {
      this.$root.$on('openStockModal', _ => {
        if (this.$refs['StockModal']) {
          this.$refs['StockModal'].show();
        }
      });
    },
    beforeDestroy() {
      this.$root.$off('openStockModal');
    }
  }
</script>

<style lang="scss">
  .selected-info__item {
    font-weight: bold;
    font-size: 13px;
  }

  .grid {
    height: 500px;
  }

  .input-group {
    display: flex;
    align-items: center;
    box-sizing: border-box;
    border: 1px solid #b5b1b1;
    border-radius: 5px;
    height: 30px;
    width: 25%;
    padding: 0 2px;

    i {
      cursor: pointer;
      font-size: 18px;
    }

    input {
      border: none;
      height: 28px;
      min-height: 28px;

      &:focus {
        box-shadow: none;
        outline: none;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/modals/StockModal.vue